import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EMPTY = { name: "", title: "", provider_type: "PHYSICIAN", specialty: "", license_no: "", clinic_name: "", location: "", service_kinds: [],
  schedule: { days: [1, 2, 3, 4, 5], start: "09:00", end: "17:00", slot_minutes: 30 }, active: true };

function ProviderForm({ initial, onDone }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [kinds, setKinds] = useState((initial?.service_kinds || []).join(", "));
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setS = (k, v) => setF({ ...f, schedule: { ...f.schedule, [k]: v } });
  const toggleDay = (d) => setS("days", f.schedule.days.includes(d) ? f.schedule.days.filter((x) => x !== d) : [...f.schedule.days, d].sort());
  const save = async (e) => {
    e.preventDefault();
    const { id, created_at, updated_at, ...rest } = f;
    const body = { ...rest, service_kinds: kinds.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean), schedule: { ...f.schedule, slot_minutes: Number(f.schedule.slot_minutes) } };
    try { id ? await api.put(`/admin/providers/${id}`, body) : await api.post("/admin/providers", body); toast.success("Provider saved"); onDone(); }
    catch (err) { setError(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="space-y-3" data-testid="provider-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name"><input className="field" value={f.name} onChange={set("name")} required data-testid="provider-name-input" /></Field>
        <Field label="Type">
          <select className="field" value={f.provider_type} onChange={set("provider_type")} data-testid="provider-type-select">
            {["PHYSICIAN", "CLINIC", "LABORATORY", "OTHER"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Title"><input className="field" value={f.title || ""} onChange={set("title")} data-testid="provider-title-input" /></Field>
        <Field label="Specialty"><input className="field" value={f.specialty || ""} onChange={set("specialty")} data-testid="provider-specialty-input" /></Field>
        <Field label="License no."><input className="field" value={f.license_no || ""} onChange={set("license_no")} data-testid="provider-license-input" /></Field>
        <Field label="Clinic"><input className="field" value={f.clinic_name || ""} onChange={set("clinic_name")} data-testid="provider-clinic-input" /></Field>
      </div>
      <Field label="Location"><input className="field" value={f.location || ""} onChange={set("location")} data-testid="provider-location-input" /></Field>
      <Field label="Service kinds (comma separated)"><input className="field font-mono" value={kinds} onChange={(e) => setKinds(e.target.value)} data-testid="provider-kinds-input" /></Field>
      <Field label="Schedule days">
        <div className="flex flex-wrap gap-1.5">{DAYS.map((d, i) => (
          <button type="button" key={d} onClick={() => toggleDay(i + 1)} data-testid={`provider-day-${d}`}
            className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${f.schedule.days.includes(i + 1) ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-600"}`}>{d}</button>
        ))}</div>
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Start"><input className="field" type="time" value={f.schedule.start} onChange={(e) => setS("start", e.target.value)} data-testid="provider-start-input" /></Field>
        <Field label="End"><input className="field" type="time" value={f.schedule.end} onChange={(e) => setS("end", e.target.value)} data-testid="provider-end-input" /></Field>
        <Field label="Slot (min)"><input className="field" type="number" min="10" value={f.schedule.slot_minutes} onChange={(e) => setS("slot_minutes", e.target.value)} data-testid="provider-slot-input" /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="provider-active-check" /> Active</label>
      <ErrorBox error={error} />
      <button className="btn-primary w-full" data-testid="provider-save-button">Save provider</button>
    </form>
  );
}

export default function Providers() {
  const { data, loading, reload } = useFetch("/admin/providers");
  const [editing, setEditing] = useState(null);
  if (loading && !data) return <Spinner />;
  return (
    <div>
      <PageHead eyebrow="Service providers" title="Providers & clinics" sub="Assign providers to packages and benefits. Link a provider login under Staff Accounts."
        actions={<button className="btn-primary" onClick={() => setEditing({})} data-testid="add-provider-button"><Plus size={15} /> Add provider</button>} />
      <div className="surface overflow-x-auto">
        <table className="data-table" data-testid="providers-table">
          <thead><tr><th>Name</th><th>Type</th><th>Specialty</th><th>Schedule</th><th>Status</th><th /></tr></thead>
          <tbody>{data?.map((p) => (
            <tr key={p.id} data-testid={`provider-row-${p.id}`}>
              <td className="font-semibold">{p.name}</td><td className="text-xs">{p.provider_type}</td><td className="text-xs">{p.specialty}</td>
              <td className="text-xs">{p.schedule.days.map((d) => DAYS[d - 1]).join(" ")} · {p.schedule.start}–{p.schedule.end}</td>
              <td><StatusBadge status={p.active ? "ACTIVE" : "INACTIVE"} /></td>
              <td><button className="btn-ghost btn-sm" onClick={() => setEditing(p)} data-testid={`edit-provider-${p.id}`}><Pencil size={13} /></button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-3xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit provider" : "Add provider"}</DialogTitle></DialogHeader>
          {editing && <ProviderForm initial={editing.id ? editing : null} onDone={() => { setEditing(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
